import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    authAPI
      .getSession()
      .then((currentSession) => {
        if (!mounted) return;
        setSession(currentSession);
      })
      .catch(() => {
        if (mounted) setSession(null);
      })
      .finally(() => {
        if (mounted) setIsLoading(false);
      });

    const { data } = authAPI.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setIsLoading(false);
    });

    return () => {
      mounted = false;
      data?.subscription?.unsubscribe();
    };
  }, []);

  const login = useCallback(async ({ email, password }) => {
    try {
      const { session: nextSession } = await authAPI.login({ email, password });
      setSession(nextSession);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err?.message || 'Login failed' };
    }
  }, []);

  const loginWithGoogle = useCallback(async () => {
    try {
      await authAPI.loginWithGoogle();
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err?.message || 'Google login failed' };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } finally {
      setSession(null);
    }
  }, []);

  const value = useMemo(
    () => ({
      isAuthenticated: Boolean(session),
      isLoading,
      login,
      loginWithGoogle,
      logout,
      session,
      user: session?.user ?? null,
    }),
    [session, isLoading, login, loginWithGoogle, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
